import {MockedProvider, MockedResponse} from '@apollo/client/testing';
import {DocumentNode} from 'graphql';
import React from 'react';

import {getApolloClient} from 'graphql-client/ApolloClient';
import QueryWrapper from 'graphql-client/QueryWrapper';

export interface MockGraphQLProviderProps {
  mocks: Array<MockedResponse>;
  children: React.ReactElement;
}

/**
 * Wrapper around Apollo's <MockedProvider> that provides an important default (a schema-aware cache).
 *
 * This uses a user-defined `mocks` mapping (GQL request -> GQL response) to determine what queries in child
 * components receive. Notably: queries with arguments will hard error unless the exact request with the
 * exact arguments was specified in `mocks`. To dynamically generate responses by mocking resolvers, see
 * `reduxMount` and its `mockResolvers` argument.
 *
 * We could consider threading through other props, though we haven't needed that so far.
 *
 * Usage:
 *    <MockGraphQLProvider mocks={...}>
 *      <MyGraphQLComponent />
 *    </MockGraphQLProvider>
 *
 * For the `mocks` prop, see https://www.apollographql.com/docs/react/recipes/testing/#testing-final-state
 */
export default function MockGraphQLProvider({mocks, children}: MockGraphQLProviderProps): JSX.Element {
  // Pass empty resolvers to support client directives: https://stackoverflow.com/a/55970913
  return (
    <MockedProvider mocks={mocks} cache={getApolloClient().cache} resolvers={{}}>
      {children}
    </MockedProvider>
  );
}

export interface TypesafeMockedResponse extends MockedResponse {
  __typesafe: true;
}

type NoInfer<T> = [T][T extends unknown ? 0 : never];

type TNoInferData<TData> = Omit<NoInfer<TData>, '__typename'>;

export function makeTypesafeQueryMock<
  TData = 'No type specified',
  TVariables extends {} | undefined = undefined,
>({
  query,
  variables,
  data,
}: {
  query: DocumentNode;
  variables?: TVariables;
  data: TNoInferData<TData>;
}): TypesafeMockedResponse {
  return {
    __typesafe: true,
    request: {query, variables},
    result: {data: {__typename: 'Query', ...data}},
  };
}

interface MockedResponseData<TData, TVariables = undefined> {
  variables?: TVariables;
  data: TData;
}

export function makeTypesafeQueryMocks<
  TData = 'No type specified',
  TVariables extends {} | undefined = undefined,
>({
  query,
  responses,
}: {
  query: DocumentNode;
  responses: Array<MockedResponseData<TNoInferData<TData>, TVariables>>;
}): Array<TypesafeMockedResponse> {
  return responses.map(({data, variables}) =>
    makeTypesafeQueryMock<TData, TVariables>({query, variables, data})
  );
}

/**
 * Similar to makeTypesafeQueryMocks, except allow the response to be dynamic.
 *
 * Useful to mock a query that is refetched, before and after a significant event.
 *
 * @param params.count - number of times the query will be mocked.
 *   This is a workaround as a mock can only be consumed once.
 * @param params.data - function that returns the data. In practice, this should read closured data that is
 *   provided via a stub defined in the test. See example.
 *
 * @example
 * let serverName = 'foo';
 * stubDAO(FooDAO, 'updateName').callsFake((newName): Promise<void> => {
 *   serverName = newName.toUpperCase();
 * });
 * const wrapper = await graphQLMount(<MyFoo id="foo_1" />, {
 *   mocks: [
 *     ...makeDynamicTypesafeQueryMocks({
 *       query: FOO_QUERY,
 *       variables: {id: "foo_1"},
 *       count: 2,
 *       data: () => ({
 *         foo: {__typename: "FooNode", name: serverName}
 *       }),
 *     )})
 *   ],
 * });
 * const harness = new FooHarness(wrapper);
 * await harness.setName('Hello'); // Submit a form that hits FooDAO.updateName and refetches data.
 * expect(harness.getRenderedName()).equal('HELLO');
 */
export function makeDynamicTypesafeQueryMocks<
  TData = 'No type specified',
  TVariables extends {} | undefined = undefined,
>(params: {
  query: DocumentNode;
  variables?: TVariables;
  data: () => TData;
  count: number;
}): Array<TypesafeMockedResponse> {
  return [...Array(params.count).keys()].map(() => ({
    __typesafe: true,
    request: {query: params.query, variables: params.variables},
    result: () => ({data: {__typename: 'Query', ...params.data()}}),
  }));
}

interface BaseTypesafeProviderProps<TData, TVariables> {
  children: JSX.Element;
}

interface SingleMockTypesafeProviderProps<TData, TVariables>
  extends BaseTypesafeProviderProps<TData, TVariables>,
    QueryWrapper<TData, TVariables> {
  variables?: TVariables;
  data: TData;
}

interface SingleQueryMultiResponseTypesafeProviderProps<TData, TVariables>
  extends BaseTypesafeProviderProps<TData, TVariables>,
    QueryWrapper<TData, TVariables> {
  responses: Array<MockedResponseData<TData, TVariables>>;
}

export interface MultiQueryTypesafeProviderProps<TData, TVariables>
  extends BaseTypesafeProviderProps<TData, TVariables> {
  mocks: Array<TypesafeMockedResponse>;
}

export type TypesafeMockGraphQLProviderProps<TData, TVariables> =
  | SingleMockTypesafeProviderProps<TData, TVariables>
  | SingleQueryMultiResponseTypesafeProviderProps<TData, TVariables>
  | MultiQueryTypesafeProviderProps<TData, TVariables>;

function isSingleMockProps<TData, TVariables = undefined>(
  props: TypesafeMockGraphQLProviderProps<TData, TVariables>
): props is SingleMockTypesafeProviderProps<TData, TVariables> {
  return props.hasOwnProperty('data');
}

function isSingleQueryMultiResponseMockProps<TData, TVariables = undefined>(
  props: TypesafeMockGraphQLProviderProps<TData, TVariables>
): props is SingleQueryMultiResponseTypesafeProviderProps<TData, TVariables> {
  return props.hasOwnProperty('responses');
}

export function TypesafeMockGraphQLProvider<
  TData = 'No type specified',
  TVariables extends {} | undefined = undefined,
>(props: TypesafeMockGraphQLProviderProps<TNoInferData<TData>, TVariables>): JSX.Element {
  if (isSingleMockProps(props)) {
    const {query, variables, data} = props;
    return (
      <MockGraphQLProvider mocks={[makeTypesafeQueryMock<TData, TVariables>({query, variables, data})]}>
        {props.children}
      </MockGraphQLProvider>
    );
  } else if (isSingleQueryMultiResponseMockProps(props)) {
    const {query, responses} = props;
    return (
      <MockGraphQLProvider mocks={makeTypesafeQueryMocks<TData, TVariables>({query, responses})}>
        {props.children}
      </MockGraphQLProvider>
    );
  } else {
    const {mocks} = props;
    return <MockGraphQLProvider mocks={mocks}>{props.children}</MockGraphQLProvider>;
  }
}
